import React, { useState, Fragment } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { BookOpenIcon, UsersIcon, TargetIcon, GitMergeIcon, ArrowDownIcon, ArrowRightIcon, CheckCircleIcon, ShieldCheckIcon, WalletIcon, FileTextIcon, SmartphoneIcon, ChevronDownIcon } from 'lucide-react';
import { TopBar } from '../components/TopBar';
interface UXDocumentationProps {
  navigate: (screen: string) => void;
}
export function UXDocumentation({
  navigate
}: UXDocumentationProps) {
  const [activeFlow, setActiveFlow] = useState<string>('purchase');
  const personas = [{
    name: 'Adaeze',
    role: 'New Mother, 28',
    goal: "Save for her newborn's university education with a guaranteed safety net.",
    product: 'Future Scholar Fund',
    color: 'emerald'
  }, {
    name: 'Chidi',
    role: 'Marketing Manager, 35',
    goal: 'Protect his income and maintain his lifestyle in case of unexpected job loss.',
    product: 'Income Shield',
    color: 'blue'
  }, {
    name: 'Emeka',
    role: 'Business Owner, 42',
    goal: 'Insure his new SUV and save towards future maintenance costs.',
    product: 'AutoGuard',
    color: 'indigo'
  }, {
    name: 'Ngozi',
    role: 'Civil Servant, 55',
    goal: 'Convert her savings into a guaranteed monthly income for retirement.',
    product: 'Freedom Fund',
    color: 'purple'
  }];
  const userStories = ["As a new parent, I want to save for my child's education with a life cover guarantee, so that their future is secure even if I pass away.", 'As a salaried professional, I want to buy income protection, so that I can cover my expenses for 6 months if I lose my job involuntarily.', 'As a vehicle owner, I want to buy comprehensive motor protection and add a savings component, so I can build a fund for future repairs.', "As a plan holder, I want to submit a claim directly through the app by uploading photos, so I don't have to visit a physical branch.", 'As a user, I want to receive my temporary protection certificate instantly upon payment, so I have immediate proof of cover.'];
  const flows = {
    registration: [{
      title: 'Download & Launch',
      desc: 'User opens Skydd app',
      icon: SmartphoneIcon
    }, {
      title: 'Basic Sign Up',
      desc: 'Phone number & OTP verification',
      icon: UserIcon
    }, {
      title: 'KYC Verification',
      desc: 'BVN/NIN verified via Embedly API',
      icon: ShieldCheckIcon
    }, {
      title: 'Wallet Creation',
      desc: 'Tier 1 Skydd Wallet instantly provisioned',
      icon: WalletIcon
    }, {
      title: 'Dashboard',
      desc: 'User lands on home screen ready to explore',
      icon: HomeIcon
    }],
    purchase: [{
      title: 'Product Discovery',
      desc: 'User selects product from catalogue',
      icon: SearchIcon
    }, {
      title: 'Goal Setting',
      desc: 'Uses calculator to set target & duration',
      icon: TargetIcon
    }, {
      title: 'Plan Selection',
      desc: 'Chooses from 3 white-labeled plan tiers',
      icon: ListIcon
    }, {
      title: 'Onboarding',
      desc: 'Fills product-specific details (e.g., beneficiary)',
      icon: FileTextIcon
    }, {
      title: 'Payment',
      desc: 'Pays first contribution via Wallet or Card',
      icon: CreditCardIcon
    }, {
      title: 'Instant Certificate',
      desc: 'Receives Skydd temporary certificate',
      icon: AwardIcon
    }],
    claims: [{
      title: 'Initiate Claim',
      desc: 'Selects active plan from history',
      icon: AlertCircleIcon
    }, {
      title: 'Document Upload',
      desc: 'Uploads photos, police report, or medical docs',
      icon: UploadIcon
    }, {
      title: 'Skydd Review',
      desc: 'Internal team verifies submission completeness',
      icon: EyeIcon
    }, {
      title: 'Underwriter Liaison',
      desc: 'Skydd forwards to underwriter offline & follows up',
      icon: SendIcon
    }, {
      title: 'Status Updates',
      desc: 'User receives in-app notifications at each stage',
      icon: BellIcon
    }, {
      title: 'Payout',
      desc: 'Approved claim amount deposited to Skydd Wallet',
      icon: WalletIcon
    }]
  };
  return <div className="flex-1 overflow-y-auto pb-safe bg-slate-50" data-id="element-2934">
      <TopBar title="UX Documentation" showBack onBack={() => navigate('policies')} data-id="element-2935" />

      <div className="px-5 pt-4 pb-12" data-id="element-2936">
        {/* Header */}
        <div className="mb-8" data-id="element-2937">
          <h1 className="text-3xl font-black text-[#0D3B3B] tracking-tight mb-2" data-id="element-2938">
            Product Spec & UX
          </h1>
          <p className="text-sm text-gray-500 leading-relaxed" data-id="element-2939">
            Documentation of the Skydd Insure-Fintech platform solution,
            personas, and core user journeys.
          </p>
        </div>

        {/* Section 1: Solution Overview */}
        <section className="mb-10" data-id="element-2940">
          <div className="flex items-center gap-2 mb-4" data-id="element-2941">
            <BookOpenIcon className="w-5 h-5 text-amber-500" data-id="element-2942" />
            <h2 className="text-lg font-bold text-[#0D3B3B]" data-id="element-2943">
              Solution Overview
            </h2>
          </div>
          <div className="bg-gradient-to-br from-[#0D3B3B] to-[#092D2D] rounded-3xl p-6 text-white shadow-lg" data-id="element-2944">
            <h3 className="font-bold text-lg mb-2" data-id="element-2945">The Skydd Model</h3>
            <p className="text-sm text-emerald-100/80 leading-relaxed mb-6" data-id="element-2946">
              Skydd is a mobile-first platform that redefines personal
              protection in Nigeria by blending goal-oriented savings with a
              robust protection backbone. It transforms protection from a grudge
              purchase into a proactive tool for wealth creation.
            </p>

            <div className="space-y-4" data-id="element-2947">
              <div className="bg-white/10 rounded-2xl p-4 border border-white/10" data-id="element-2948">
                <h4 className="font-bold text-sm text-amber-400 mb-1" data-id="element-2949">
                  Protection-Backed Savings
                </h4>
                <p className="text-xs text-white/70 leading-relaxed" data-id="element-2950">
                  Every savings plan carries a protection component. If the plan
                  holder passes away, the full target amount is guaranteed to
                  the beneficiary.
                </p>
              </div>
              <div className="bg-white/10 rounded-2xl p-4 border border-white/10" data-id="element-2951">
                <h4 className="font-bold text-sm text-amber-400 mb-1" data-id="element-2952">
                  Offline Liaison Model
                </h4>
                <p className="text-xs text-white/70 leading-relaxed" data-id="element-2953">
                  No API integration with underwriters is required. Skydd issues
                  a temporary certificate instantly, then registers the plan
                  with the underwriter offline.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Section 2: Personas */}
        <section className="mb-10" data-id="element-2954">
          <div className="flex items-center gap-2 mb-4" data-id="element-2955">
            <UsersIcon className="w-5 h-5 text-amber-500" data-id="element-2956" />
            <h2 className="text-lg font-bold text-[#0D3B3B]" data-id="element-2957">
              Target Personas
            </h2>
          </div>
          <div className="flex overflow-x-auto hide-scrollbar gap-4 pb-4 -mx-5 px-5" data-id="element-2958">
            {personas.map((persona, idx) => <div key={idx} className="w-64 shrink-0 bg-white rounded-3xl p-5 border border-gray-100 shadow-sm" data-id="element-2959">
                <div className={`w-12 h-12 rounded-full bg-${persona.color}-100 flex items-center justify-center text-${persona.color}-600 font-bold text-lg mb-3`} data-id="element-2960">
                  {persona.name.charAt(0)}
                </div>
                <h3 className="font-bold text-[#0D3B3B]" data-id="element-2961">{persona.name}</h3>
                <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3" data-id="element-2962">
                  {persona.role}
                </p>
                <p className="text-sm text-gray-600 leading-relaxed mb-4" data-id="element-2963">
                  {persona.goal}
                </p>
                <div className={`inline-flex items-center px-2.5 py-1 rounded-md bg-${persona.color}-50 text-${persona.color}-700 text-[10px] font-bold uppercase tracking-wider`} data-id="element-2964">
                  {persona.product}
                </div>
              </div>)}
          </div>
        </section>

        {/* Section 3: User Stories */}
        <section className="mb-10" data-id="element-2965">
          <div className="flex items-center gap-2 mb-4" data-id="element-2966">
            <TargetIcon className="w-5 h-5 text-amber-500" data-id="element-2967" />
            <h2 className="text-lg font-bold text-[#0D3B3B]" data-id="element-2968">
              Core User Stories
            </h2>
          </div>
          <div className="bg-white rounded-3xl p-2 border border-gray-100 shadow-sm" data-id="element-2969">
            {userStories.map((story, idx) => <div key={idx} className="flex items-start gap-3 p-4 border-b border-gray-50 last:border-0" data-id="element-2970">
                <CheckCircleIcon className="w-5 h-5 text-emerald-500 shrink-0 mt-0.5" data-id="element-2971" />
                <p className="text-sm text-gray-700 leading-relaxed font-medium" data-id="element-2972">
                  {story}
                </p>
              </div>)}
          </div>
        </section>

        {/* Section 4: UX Flows */}
        <section className="mb-6" data-id="element-2973">
          <div className="flex items-center gap-2 mb-4" data-id="element-2974">
            <GitMergeIcon className="w-5 h-5 text-amber-500" data-id="element-2975" />
            <h2 className="text-lg font-bold text-[#0D3B3B]" data-id="element-2976">UX Flows</h2>
          </div>

          {/* Flow Tabs */}
          <div className="flex gap-2 mb-6 overflow-x-auto hide-scrollbar pb-1" data-id="element-2977">
            {['purchase', 'claims', 'registration'].map(flow => <button key={flow} onClick={() => setActiveFlow(flow)} className={`px-5 py-2 rounded-full text-sm font-bold capitalize whitespace-nowrap transition-all ${activeFlow === flow ? 'bg-[#0D3B3B] text-white shadow-md' : 'bg-white text-gray-500 border border-gray-200 hover:bg-gray-50'}`} data-id="element-2978">
                {flow} Flow
              </button>)}
          </div>

          {/* Flow Diagram */}
          <div className="bg-white rounded-3xl p-6 border border-gray-100 shadow-sm" data-id="element-2979">
            <AnimatePresence mode="wait" data-id="element-2980">
              <motion.div key={activeFlow} initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0,
              y: -10
            }} transition={{
              duration: 0.2
            }} className="space-y-2" data-id="element-2981">
                {flows[activeFlow as keyof typeof flows].map((step, idx, arr) => <Fragment key={idx} data-id="element-2982">
                      <div className="bg-gray-50 rounded-2xl p-4 border border-gray-100 flex items-center gap-4" data-id="element-2983">
                        <div className="w-10 h-10 rounded-xl bg-white shadow-sm flex items-center justify-center text-[#0D3B3B] shrink-0" data-id="element-2984">
                          <step.icon className="w-5 h-5" data-id="element-2985" />
                        </div>
                        <div data-id="element-2986">
                          <h4 className="font-bold text-[#0D3B3B] text-sm" data-id="element-2987">
                            {step.title}
                          </h4>
                          <p className="text-xs text-gray-500 mt-0.5" data-id="element-2988">
                            {step.desc}
                          </p>
                        </div>
                      </div>
                      {idx < arr.length - 1 && <div className="flex justify-center py-1" data-id="element-2989">
                          <ArrowDownIcon className="w-5 h-5 text-gray-300" data-id="element-2990" />
                        </div>}
                    </Fragment>)}
              </motion.div>
            </AnimatePresence>
          </div>
        </section>
      </div>
    </div>;
}
// Helper icons for the flows
function UserIcon(props: any) {
  return <svg {...props} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLineJoin="round" data-id="element-2991">
      <path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2" data-id="element-2992" />
      <circle cx="12" cy="7" r="4" data-id="element-2993" />
    </svg>;
}
function HomeIcon(props: any) {
  return <svg {...props} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLineJoin="round" data-id="element-2994">
      <path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" data-id="element-2995" />
      <polyline points="9 22 9 12 15 12 15 22" data-id="element-2996" />
    </svg>;
}
function SearchIcon(props: any) {
  return <svg {...props} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLineJoin="round" data-id="element-2997">
      <circle cx="11" cy="11" r="8" data-id="element-2998" />
      <path d="m21 21-4.3-4.3" data-id="element-2999" />
    </svg>;
}
function ListIcon(props: any) {
  return <svg {...props} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLineJoin="round" data-id="element-3000">
      <line x1="8" x2="21" y1="6" y2="6" data-id="element-3001" />
      <line x1="8" x2="21" y1="12" y2="12" data-id="element-3002" />
      <line x1="8" x2="21" y1="18" y2="18" data-id="element-3003" />
      <line x1="3" x2="3.01" y1="6" y2="6" data-id="element-3004" />
      <line x1="3" x2="3.01" y1="12" y2="12" data-id="element-3005" />
      <line x1="3" x2="3.01" y1="18" y2="18" data-id="element-3006" />
    </svg>;
}
function CreditCardIcon(props: any) {
  return <svg {...props} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLineJoin="round" data-id="element-3007">
      <rect width="20" height="14" x="2" y="5" rx="2" data-id="element-3008" />
      <line x1="2" x2="22" y1="10" y2="10" data-id="element-3009" />
    </svg>;
}
function AwardIcon(props: any) {
  return <svg {...props} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLineJoin="round" data-id="element-3010">
      <circle cx="12" cy="8" r="7" data-id="element-3011" />
      <polyline points="8.21 13.89 7 23 12 20 17 23 15.79 13.88" data-id="element-3012" />
    </svg>;
}
function AlertCircleIcon(props: any) {
  return <svg {...props} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLineJoin="round" data-id="element-3013">
      <circle cx="12" cy="12" r="10" data-id="element-3014" />
      <line x1="12" x2="12" y1="8" y2="12" data-id="element-3015" />
      <line x1="12" x2="12.01" y1="16" y2="16" data-id="element-3016" />
    </svg>;
}
function UploadIcon(props: any) {
  return <svg {...props} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLineJoin="round" data-id="element-3017">
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" data-id="element-3018" />
      <polyline points="17 8 12 3 7 8" data-id="element-3019" />
      <line x1="12" x2="12" y1="3" y2="15" data-id="element-3020" />
    </svg>;
}
function EyeIcon(props: any) {
  return <svg {...props} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLineJoin="round" data-id="element-3021">
      <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z" data-id="element-3022" />
      <circle cx="12" cy="12" r="3" data-id="element-3023" />
    </svg>;
}
function SendIcon(props: any) {
  return <svg {...props} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLineJoin="round" data-id="element-3024">
      <path d="m22 2-7 20-4-9-9-4Z" data-id="element-3025" />
      <path d="M22 2 11 13" data-id="element-3026" />
    </svg>;
}
function BellIcon(props: any) {
  return <svg {...props} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLineJoin="round" data-id="element-3027">
      <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" data-id="element-3028" />
      <path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" data-id="element-3029" />
    </svg>;
}